import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useInboxStore } from '../../stores/inbox-store';

/**
 * Snooze-dropdown met presets. Gebruikt in de ThreadDetail-header en als
 * hover-actie in ThreadList. Presets rekenen in lokale tijd (de browser van
 * kantoor draait Europe/Amsterdam).
 */

function plusHours(hours: number): Date {
  return new Date(Date.now() + hours * 60 * 60 * 1000);
}

function tomorrowAt(hour: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hour, 0, 0, 0);
  return d;
}

function nextMondayAt(hour: number): Date {
  const d = new Date();
  const day = d.getDay(); // 0=zo, 1=ma
  const daysUntilMonday = ((8 - day) % 7) || 7;
  d.setDate(d.getDate() + daysUntilMonday);
  d.setHours(hour, 0, 0, 0);
  return d;
}

export function formatSnoozeUntil(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `tot ${time}`;
  if (isTomorrow) return `tot morgen ${time}`;
  return `tot ${d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} ${time}`;
}

export function SnoozeMenu({
  conversationId,
  snoozedUntil,
  variant = 'header',
}: {
  conversationId: string;
  snoozedUntil?: string | null;
  /** 'header' = knop met label in ThreadDetail; 'compact' = icoon-only hover-actie in ThreadList. */
  variant?: 'header' | 'compact';
}) {
  const snoozeConversation = useInboxStore((s) => s.snoozeConversation);
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  const isSnoozed = !!snoozedUntil && new Date(snoozedUntil).getTime() > Date.now();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowCustom(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const doSnooze = (until: Date | null) => {
    setOpen(false);
    setShowCustom(false);
    setCustomValue('');
    void snoozeConversation(conversationId, until ? until.toISOString() : null).catch(() => {});
  };

  const presets: Array<{ label: string; getDate: () => Date }> = [
    { label: '+3 uur', getDate: () => plusHours(3) },
    { label: 'Morgen 09:00', getDate: () => tomorrowAt(9) },
    { label: 'Maandag 09:00', getDate: () => nextMondayAt(9) },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={clsx(
          variant === 'header'
            ? 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap'
            : 'p-1 rounded transition-colors',
          isSnoozed
            ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
            : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'
        )}
        title={isSnoozed && snoozedUntil ? `Gesnoozed ${formatSnoozeUntil(snoozedUntil)}` : 'Snooze — later behandelen'}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {variant === 'header' && (isSnoozed && snoozedUntil ? formatSnoozeUntil(snoozedUntil) : 'Later')}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
            Snooze tot
          </div>
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => doSnooze(p.getDate())}
              className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
            >
              {p.label}
            </button>
          ))}
          {!showCustom ? (
            <button
              type="button"
              onClick={() => setShowCustom(true)}
              className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
            >
              Aangepast…
            </button>
          ) : (
            <div className="px-3 py-1.5 flex items-center gap-1.5">
              <input
                type="datetime-local"
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                className="flex-1 text-xs border border-slate-200 rounded px-1.5 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <button
                type="button"
                disabled={!customValue || new Date(customValue).getTime() <= Date.now()}
                onClick={() => customValue && doSnooze(new Date(customValue))}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:text-slate-300"
              >
                OK
              </button>
            </div>
          )}
          {isSnoozed && (
            <>
              <div className="border-t border-slate-100 my-1" />
              <button
                type="button"
                onClick={() => doSnooze(null)}
                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                Snooze opheffen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
